import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import MotionPhotosAutoRoundedIcon from "@mui/icons-material/MotionPhotosAutoRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function PhaserLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Phaser in Music Production: What It Is and How It Works",
      description:
        "Learn what a phaser does, how phase shifting creates its sweeping sound, how it differs from flanger and chorus, and where phasers are used in music production.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/phaser",
      articleSection: "Audio Education",
      keywords: [
        "phaser",
        "what does a phaser do",
        "phaser effect",
        "phaser vs flanger",
        "phaser vs chorus",
        "guitar phaser",
        "audio modulation",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A phaser creates a sweeping, swirling sound by shifting phase in parts of the signal and moving those changes over time with modulation.",
      icon: <WavesRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Phasers add motion, texture, and psychedelic character, making sounds feel more alive, animated, and spatially interesting.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Electric guitar, synths, keys, drums, vocals, bass, and creative sound design across rock, funk, electronic music, and beyond.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const phaserTypes = [
    {
      name: "Classic Analog Phaser",
      description:
        "Known for smooth, musical sweeps and vintage character, especially in guitar pedal designs from classic eras.",
    },
    {
      name: "Multi-Stage Phaser",
      description:
        "Uses multiple phase-shifting stages to create deeper or more complex notch patterns and movement.",
    },
    {
      name: "Stereo Phaser",
      description:
        "Spreads the modulation between left and right channels for a wider, more immersive effect.",
    },
    {
      name: "Feedback Phaser",
      description:
        "Feeds part of the output back into the effect, increasing intensity and making the sweep more pronounced.",
    },
    {
      name: "Envelope / Dynamic Phaser",
      description:
        "Changes the phasing based on playing dynamics rather than only using an LFO, which can feel more expressive and touch-responsive.",
    },
    {
      name: "Digital Experimental Phaser",
      description:
        "Can go beyond vintage pedal behavior into sharper, more extreme, or more precisely controllable modulation textures.",
    },
  ];

  const phaserConcepts = [
    {
      name: "Phase Shift",
      description:
        "A phaser changes the timing relationship of parts of the signal without using a long delay the way some other modulation effects do.",
    },
    {
      name: "Notches",
      description:
        "When the shifted signal is mixed back with the dry signal, cancellations occur at certain frequencies, creating moving notches in the spectrum.",
    },
    {
      name: "LFO Modulation",
      description:
        "An LFO moves those notches up and down over time, which creates the characteristic sweeping motion.",
    },
    {
      name: "Texture and Motion",
      description:
        "The result can sound subtle and silky or deep and psychedelic depending on the speed, depth, stages, and feedback.",
    },
  ];

  const controls = [
    {
      name: "Rate / Speed",
      description:
        "Controls how fast the phaser sweeps. Slow settings can sound smooth and dreamy, while faster settings feel more animated and obvious.",
    },
    {
      name: "Depth",
      description:
        "Controls how strong the modulation is, affecting how dramatic the movement sounds.",
    },
    {
      name: "Feedback / Resonance",
      description:
        "Feeds some of the output back into the effect, emphasizing the sweep and making the phasing more intense.",
    },
    {
      name: "Stages",
      description:
        "Changes the number of phase-shifting stages, which affects the complexity and character of the sweep.",
    },
    {
      name: "Mix",
      description:
        "Blends the dry and effected signals, which is useful for subtle enhancement or more obvious modulation.",
    },
    {
      name: "Center / Manual / Frequency",
      description:
        "Some phasers let you choose the region where the sweep is focused, which changes the tonal character dramatically.",
    },
  ];

  const commonUses = [
    "Adding swirl and movement to electric guitar",
    "Giving synth pads and keys a more animated, psychedelic feel",
    "Creating funky, expressive modulation on clean rhythm parts",
    "Adding subtle motion to vocals or background textures",
    "Enhancing bass with controlled movement in the mids or highs",
    "Designing evolving soundscapes and special effects",
  ];

  const bestPractices = [
    "Match the phaser speed to the mood of the song. Slow movement often feels wide and musical, while fast movement feels more obvious and energetic.",
    "Use feedback carefully. A little can add character, but too much can dominate the sound quickly.",
    "Check the effect in context, because a phaser that sounds exciting in solo can take up too much space in the mix.",
    "Try low mix settings for subtle movement rather than obvious modulation.",
    "Consider stereo phasing for width, but check mono compatibility if the effect is important to the arrangement.",
  ];

  const relatedLinks = [
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "Learn how chorus creates width and shimmer in a different way than the spectral sweeping of a phaser.",
    },
    {
      title: "Delay",
      to: "/learning/delay",
      description:
        "Explore how delay repeats sound over time, which is very different from the moving comb-like motion of phasing.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "See how saturation adds harmonic color, which can pair well with modulation effects like phaser.",
    },
    {
      title: "Reverb",
      to: "/learning/reverb",
      description:
        "Understand how reverb adds space, while a phaser adds motion and spectral movement.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Phaser | Parish Audio Learning"
        description="Learn what a phaser does, how phase shifting creates its sweeping sound, how it differs from flanger and chorus, and where phasers are used in music production."
        canonical="https://parishaudio.com/learning/phaser"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Phaser in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                A phaser is a modulation effect known for its sweeping, swirling sound.
                It works by shifting the phase of parts of the signal and then mixing that
                altered signal back with the original. As the phase relationships move over
                time, certain frequencies cancel and reappear, creating the characteristic
                motion that makes a phaser feel animated and expressive.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                Phasers are widely used on guitar, synths, keyboards, bass, and sound
                design elements. They can be subtle and smooth or intense and psychedelic.
                In many cases, a phaser adds a sense of life and movement without changing
                the rhythm or pitch of the source.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.97rem" }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                What is a phaser?
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                A phaser is an audio effect that creates moving notches in the frequency
                spectrum by changing phase relationships and modulating them over time. The
                result is a smooth, sweeping tone that can add shimmer, swirl, depth, or a
                distinctly psychedelic character.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                Unlike a delay, a phaser does not mainly rely on obvious repeated echoes.
                Unlike chorus, it does not mainly aim to simulate multiple slightly detuned
                voices. Instead, it reshapes the spectral balance dynamically in a way that
                creates motion and character.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                Because of that, phasers are often loved for their musical movement. A phaser
                can make a simple chord progression feel fluid, a synth pad feel alive, or a
                guitar line feel more expressive without completely changing the source.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How a phaser works
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {phaserConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                The movement usually comes from an LFO, or low-frequency oscillator, which
                slowly changes where the phasing occurs. That moving pattern is what creates
                the classic sweep. Depending on the design, the effect can sound silky,
                watery, vocal-like, funky, or deeply psychedelic.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common types of phasers
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {phaserTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Phaser vs chorus and flanger
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Similar family, different kind of movement
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A chorus usually creates width and shimmer by using short delay-based
                    modulation and slight pitch-style variation. A flanger tends to create
                    a more pronounced comb-filtered sweep. A phaser creates movement mainly
                    through changing phase relationships and moving notches.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In listening terms, phasers often sound smoother and more fluid than
                    flangers, and more spectral or swirly than chorus. The exact line
                    between them can blur in some designs, but their core behaviors are
                    still different.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common phaser controls
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How phasers are used in production
              </Typography>

              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                A phaser can be used as a featured effect or as a subtle layer of motion.
                On guitar, it can add funk, sweep, or vintage character. On synths and
                pads, it can keep sustained sounds from feeling static. In sound design,
                it can help transform ordinary tones into something more futuristic or dreamlike.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Best practices for using a phaser
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Why phasers feel so musical
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  {[
                    "They add motion without necessarily overwhelming the original note or chord.",
                    "They can be subtle enough for texture or strong enough to become a signature sound.",
                    "They often pair well with guitar, synths, reverb, and delay because they add movement rather than only space or gain.",
                    "Their sweeping behavior naturally creates emotional contrast, from smooth and dreamy to funky and dramatic.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <MotionPhotosAutoRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Final thoughts
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                A phaser is one of the most expressive modulation effects in music production.
                It can add subtle motion, vintage character, psychedelic sweep, or dramatic
                texture depending on how it is used.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                Whether it is gently animating a background texture or becoming a major part
                of a guitar or synth sound, a phaser is valuable because it adds movement in
                a way that feels musical and alive.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}